import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'antd';
import datePickerLocaleEn from 'antd/es/date-picker/locale/en_US';
import datePickerLocaleEs from 'antd/es/date-picker/locale/es_ES';
import c from 'classnames';
import { range } from 'lodash';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import { Dayjs } from '@lgg/isomorphic/dayjs';
import { getDatesDifferenceInUnits } from '@lgg/isomorphic/i18n/date-utils';
import { addYears, subYears } from '@lgg/isomorphic/utils/date-operations';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import {
  CalendarDateItem,
  CalendarHeader,
  DateBox,
  StyledCalendar,
} from 'src/components/general/inputs/custom-date-pickers/lgg-calendar';
import { LggDatePickerInput } from 'src/components/general/inputs/custom-date-pickers/lgg-date-picker-input';
import { FooterButton } from 'src/components/general/inputs/custom-date-pickers/shared';
import { LggDatePicker } from 'src/components/general/inputs/date-picker';
import { Select } from 'src/components/general/inputs/select/select';
import { NoScrollOverlay } from 'src/components/general/inputs/shared';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useDateHelpers } from 'src/hooks/use-date-helpers';
import { useVisible } from 'src/hooks/use-visible';
import { LggModal } from '../../modals/lgg-modal';
import { MobileWheelTimePicker } from './mobile-wheel-time-picker';

const HeaderContainer = styled(FlexRow)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
  color: ${({ theme }) => theme.colors.carbon};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  line-height: 18px;
  padding: 15px;
`;

const BodyContainer = styled(FlexRow)`
  padding: 15px 20px 45px;
  gap: 10px;

  ${up('md')} {
    padding: 15px;
  }

  & > * {
    flex: 1;
  }
`;

const StyledSelect = styled(Select)`
  height: 34px;
` as unknown as typeof Select;

const PresetOptionsContainer = styled(FlexColumn)`
  background: ${({ theme }) => theme.colors.alabaster};
  border-right: 1px solid ${({ theme }) => theme.colors.koala};
  justify-content: space-between;
  padding: 10px;
  width: 180px;
`;

const PresetOptionContainer = styled(FlexRow)`
  justify-content: space-between;
  padding: 5px 5px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.porcelain};
  }
`;

const PresetOptionLabel = styled.p`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 14px;
  margin: 0;
  text-transform: capitalize;
`;

const PresetOptionPreview = styled.p`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  letter-spacing: -0.11px;
  line-height: 14px;
  margin: 0;
  text-align: right;
`;

const FooterContainer = styled(FlexRow)`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.koala};
  gap: 10px;
  justify-content: space-between;
  padding: 10px;
  width: 100%;

  ${up('md')} {
    justify-content: flex-end;
  }
`;

const datePickerCellStyles = css`
  .ant-picker-cell {
    color: ${({ theme }) => theme.colors.geyser};
    font-family: ${({ theme }) => theme.font.regular};
    font-size: 14px;
    max-height: 30px;
    text-align: center;

    .ant-picker-cell-inner {
      align-items: center;
      border-radius: 4px;
      display: flex;
      height: 30px;
      justify-content: center;
      margin: 0 auto;

      &.ant-picker-calendar-date-today {
        background: ${({ theme }) => theme.colors.porcelain};
        border-radius: 4px;
      }
    }

    &.ant-picker-cell-in-view {
      color: ${({ theme }) => theme.colors.smalt};

      &.ant-picker-cell-selected {
        .ant-picker-cell-inner {
          max-width: 50px;
          background: ${({ theme }) => theme.colors.gogo};
          color: ${({ theme }) => theme.colors.white};
        }
      }
    }
  }
`;

const CalendarContainer = styled.div`
  width: 282px;

  .ant-picker-month-panel {
    ${datePickerCellStyles};
  }
`;

const DateAndTimePickerMobileContainer = styled(FlexColumn)`
  width: 340px;
`;

const DecadePickerContainer = styled.div`
  border-radius: 4px;
  border: none;
  overflow: hidden;
  position: relative;

  .ant-picker-year-panel {
    .ant-picker-header {
      display: none;
    }

    .ant-picker-body {
      margin: 5px 15px;
      padding: 0;

      .ant-picker-content {
        height: 231px;
      }

      ${datePickerCellStyles};
    }
  }

  .ant-picker {
    border: none;
    box-shadow: none;
    padding: 0;

    input {
      display: none;
    }

    .ant-picker-suffix {
      display: none;
    }
  }

  .ant-picker-panel-container {
    .ant-picker-header {
      display: none;
    }
  }
`;

const DateTimePickerCalendar = styled(StyledCalendar)`
  &.ant-picker-calendar {
    .ant-picker-panel {
      .ant-picker-date-panel {
        .ant-picker-body {
          padding: 5px 10px;

          ${up('md')} {
            padding: 0;
          }
        }
      }

      .ant-picker-month-panel {
        .ant-picker-body {
          padding: 0;
        }
      }
    }
  }
`;

const ModalDatePickerModal = styled(LggModal)`
  margin: 0 auto;
  padding-bottom: 0;
  max-width: max-content;
  height: max-content;

  .ant-modal-close {
    display: none;
  }
`;

const DEFAULT_INPUT_DATE_FORMAT = 'MMM D, YYYY. h:mm A';

type TimeOption = { value: number; label: string };

const normalizeTimeText = (text: string) => text.replace(/\s/g, '').toLowerCase();

// Accepts `h:mm` (12h or 24h) optionally followed by a meridiem, e.g. `12:13`, `9:05 pm`, `17:40`
const CUSTOM_TIME_REGEX = /^(\d{1,2}):(\d{2})/;

const parseCustomTimeMinutes = (searchText: string): number[] => {
  const result = CUSTOM_TIME_REGEX.exec(searchText.trim());

  if (!result) {
    return [];
  }

  const hours = Number(result[1]);
  const minutes = Number(result[2]);

  if (hours > 23 || minutes > 59) {
    return [];
  }

  // Without a 12h hour we can't tell AM from PM, so both are offered
  if (hours >= 1 && hours <= 12) {
    const hoursFromMidnight = hours % 12;

    return [hoursFromMidnight * 60 + minutes, (hoursFromMidnight + 12) * 60 + minutes];
  }

  return [hours * 60 + minutes];
};

// Behaves like an autocomplete: presets whose label starts with the typed text are shown,
// and if none matches, the typed time itself is offered as an option.
const getTimeOptionsMatchingSearch = ({
  presetTimeOptions,
  searchText,
  getTimeOption,
}: {
  presetTimeOptions: TimeOption[];
  searchText: string;
  getTimeOption: (totalMinutes: number) => TimeOption;
}): TimeOption[] => {
  const normalizedSearchText = normalizeTimeText(searchText);

  if (!normalizedSearchText) {
    return presetTimeOptions;
  }

  const matchesSearch = (option: TimeOption) =>
    normalizeTimeText(option.label).startsWith(normalizedSearchText);

  const matchingPresetOptions = presetTimeOptions.filter(matchesSearch);

  if (matchingPresetOptions.length > 0) {
    return matchingPresetOptions;
  }

  const customTimeOptions = parseCustomTimeMinutes(searchText).map(getTimeOption);
  const matchingCustomTimeOptions = customTimeOptions.filter(matchesSearch);

  // 24h input (e.g. `17:40`) won't match its 12h label, but it's still a valid time
  return matchingCustomTimeOptions.length > 0
    ? matchingCustomTimeOptions
    : customTimeOptions;
};

export type LggDateTimePickerProps = {
  onApply: (date: Date | null) => void;
  inputValue?: Date;
  showPresetOptions?: boolean;
  allowTimeSelection?: boolean;
  label?: string;
  inputDateFormat?: string;
  placeholder?: string;
  testId?: string;
  hasError?: boolean;
  disabledDate?: (date: Dayjs) => boolean;
  showSuffixIcon?: boolean;
  allowClear?: boolean;
  renderValue?: (date: Dayjs) => React.ReactNode;
};

export type DatePickerMode = 'decade' | 'month' | 'year';

export const LggDateTimePicker = ({
  showPresetOptions = false,
  allowTimeSelection = false,
  inputValue,
  onApply,
  label,
  inputDateFormat,
  placeholder,
  testId,
  hasError,
  disabledDate,
  showSuffixIcon = true,
  allowClear = true,
}: LggDateTimePickerProps) => {
  const visibilityHandler = useVisible();
  const breakpointUpMd = useBreakpoint(up('md'));
  const datePickerVisibilityHandler = useVisible();
  const [datePickerMode, setDatePickerMode] = useState<DatePickerMode>('month');
  const [date, setDate] = useState<Dayjs | undefined>(undefined);
  // Minutes from midnight, not restricted to the 15 minutes presets
  const [desktopTimeMinutes, setDesktopTimeMinutes] = useState<number | undefined>(
    undefined,
  );
  const [timeSearchText, setTimeSearchText] = useState('');
  const [mobileDateWithTime, setMobileDateWithTime] = useState<Dayjs | undefined>(
    undefined,
  );
  const {
    initDayJsDate,
    subMonths,
    format,
    addMonths,
    addDays,
    nextMonday,
    addWeeks,
    isSameDay,
    startOfDay,
    addMinutes,
  } = useDateHelpers();
  const decadePickerContainerRef = useRef<HTMLDivElement>(null);

  const {
    i18n: { language },
    t,
  } = useTranslation(['common']);

  const datePickerLocale = useMemo(
    () => (language.startsWith('es') ? datePickerLocaleEs : datePickerLocaleEn),
    [language],
  );

  const inputFormat = useMemo(
    () => inputDateFormat ?? DEFAULT_INPUT_DATE_FORMAT,
    [inputDateFormat],
  );

  useEffect(() => {
    if (inputValue) {
      const parsedInputValue = initDayJsDate(inputValue);
      setDate(parsedInputValue);
      const { minutes } = getDatesDifferenceInUnits(startOfDay(inputValue), inputValue);

      setMobileDateWithTime(parsedInputValue);
      setDesktopTimeMinutes(minutes);
    } else {
      setDate(undefined);
      setMobileDateWithTime(undefined);
      setDesktopTimeMinutes(undefined);
    }
  }, [initDayJsDate, startOfDay, inputValue]);

  const setPickerDate = useCallback(
    (selectedDate: Date) => {
      setDate(initDayJsDate(selectedDate));

      if (datePickerMode === 'month') {
        if (!allowTimeSelection) {
          onApply(startOfDay(selectedDate));
          visibilityHandler.close();
          datePickerVisibilityHandler.close();
        } else {
          datePickerVisibilityHandler.close();
        }
      } else {
        setDatePickerMode('month');
      }
    },
    [
      allowTimeSelection,
      datePickerMode,
      datePickerVisibilityHandler,
      initDayJsDate,
      onApply,
      startOfDay,
      visibilityHandler,
    ],
  );

  const onSubmit = useCallback(() => {
    if (!date) {
      return;
    }

    const minutesFromMidnight = breakpointUpMd
      ? desktopTimeMinutes ?? 0
      : mobileDateWithTime
      ? getDatesDifferenceInUnits(
          startOfDay(mobileDateWithTime.toDate()),
          mobileDateWithTime.toDate(),
        ).minutes
      : 0;

    const newDateWithMinutes = addMinutes(startOfDay(date.toDate()), minutesFromMidnight);

    onApply(newDateWithMinutes || null);
    visibilityHandler.close();
  }, [
    date,
    breakpointUpMd,
    desktopTimeMinutes,
    mobileDateWithTime,
    onApply,
    visibilityHandler,
    startOfDay,
    addMinutes,
  ]);

  const getCalendarModeMappingValue = (mode: DatePickerMode) => {
    // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
    return match(mode)
      .with('decade', () => 'year' as const)
      .with('month', () => 'month' as const)
      .with('year', () => 'year' as const)
      .exhaustive();
  };

  const presetOptionsList = useMemo(() => {
    if (!showPresetOptions || !breakpointUpMd) {
      return null;
    }

    const currentDate = initDayJsDate(new Date()).toDate();

    const baseOptions = [
      {
        label: t('common:pointInTime.today'),
        previewFormat: 'ddd',
        value: currentDate,
        testId: 'date-time-picker-preset-option-today',
      },
      {
        label: t('common:pointInTime.tomorrow'),
        previewFormat: 'ddd',
        value: addDays(currentDate, 1),
        testId: 'date-time-picker-preset-option-tomorrow',
      },
      {
        label: t('common:pointInTime.thisWeekend'),
        previewFormat: 'ddd',
        value: addDays(
          currentDate,
          currentDate.getDay() === 0 ? 0 : 6 - currentDate.getDay(),
        ),
        testId: 'date-time-picker-preset-option-this-weekend',
      },
      {
        label: t('common:pointInTime.nextWeek'),
        previewFormat: 'ddd',
        value: nextMonday(currentDate),
        testId: 'date-time-picker-preset-option-next-week',
      },
      {
        label: t('common:pointInTime.nextWeekend'),
        previewFormat: 'D MMM',
        value: addDays(nextMonday(currentDate), 5),
        testId: 'date-time-picker-preset-option-next-weekend',
      },
      {
        label: t('common:pointInTime.twoWeeks'),
        previewFormat: 'D MMM',
        value: addWeeks(currentDate, 2),
        testId: 'date-time-picker-preset-option-two-weeks',
      },
      {
        label: t('common:pointInTime.fourWeeks'),
        previewFormat: 'D MMM',
        value: addWeeks(currentDate, 4),
        testId: 'date-time-picker-preset-option-four-weeks',
      },
    ];

    return (
      <PresetOptionsContainer>
        {baseOptions.map((option) => (
          <PresetOptionContainer
            key={option.label}
            onClick={() => {
              setPickerDate(option.value);
            }}
            data-lgg-id={option.testId}
          >
            <PresetOptionLabel>{option.label}</PresetOptionLabel>
            <PresetOptionPreview>
              {format(option.value, option.previewFormat)}
            </PresetOptionPreview>
          </PresetOptionContainer>
        ))}
      </PresetOptionsContainer>
    );
  }, [
    addDays,
    addWeeks,
    breakpointUpMd,
    format,
    initDayJsDate,
    nextMonday,
    setPickerDate,
    showPresetOptions,
    t,
  ]);

  const calendarOverlay = useMemo(() => {
    const calendarDate = date || initDayJsDate(new Date());

    return (
      <FlexRow>
        {presetOptionsList}
        <CalendarContainer>
          <CalendarHeader
            allowModeChange={true}
            headerDate={calendarDate.toDate()}
            onBack={() => {
              match(datePickerMode)
                .with('decade', () => {
                  setDate(initDayJsDate(subYears(calendarDate.toDate(), 10)));
                })
                .with('year', () => {
                  setDate(initDayJsDate(subYears(calendarDate.toDate(), 1)));
                })
                .otherwise(() => {
                  setDate(initDayJsDate(subMonths(calendarDate.toDate(), 1)));
                });
            }}
            onNext={() => {
              match(datePickerMode)
                .with('decade', () => {
                  setDate(initDayJsDate(addYears(calendarDate.toDate(), 10)));
                })
                .with('year', () => {
                  setDate(initDayJsDate(addYears(calendarDate.toDate(), 1)));
                })
                .otherwise(() => {
                  setDate(initDayJsDate(addMonths(calendarDate.toDate(), 1)));
                });
            }}
            baseTestId="lgg-date-time"
            mode={datePickerMode}
            onChangeMode={setDatePickerMode}
          />
          {datePickerMode === 'decade' ? (
            <DecadePickerContainer ref={decadePickerContainerRef}>
              <LggDatePicker.YearPicker
                open
                transitionName=""
                onSelect={(date) => {
                  if (date) {
                    const targetYear = date.year();
                    const targetdate = calendarDate.set('year', targetYear);
                    setDate(targetdate);
                    setDatePickerMode('month');
                  }
                }}
                panelRender={(content) => {
                  return createPortal(
                    content,
                    decadePickerContainerRef.current ?? document.body,
                  );
                }}
                value={calendarDate}
              />
            </DecadePickerContainer>
          ) : (
            <DateTimePickerCalendar
              fullscreen={false}
              mode={getCalendarModeMappingValue(datePickerMode)}
              headerRender={() => null}
              onSelect={(date) => {
                setPickerDate(date.toDate());
              }}
              disabledDate={disabledDate}
              locale={datePickerLocale}
              value={date}
              dateFullCellRender={(cellDate) => {
                const currentDateDateObject = cellDate.toDate();
                const formattedDate = match(datePickerMode)
                  .with('year', () => format(currentDateDateObject, 'YYYY'))
                  .otherwise(() => format(currentDateDateObject, 'D'));
                const isInView = cellDate.isSame(calendarDate, 'month');

                return (
                  <CalendarDateItem
                    data-lgg-id={
                      isInView ? `custom-date-picker-date-${formattedDate}` : ''
                    }
                    className={c({
                      'selected-date':
                        date && isSameDay(currentDateDateObject, date.toDate()),
                    })}
                  >
                    <DateBox>{formattedDate}</DateBox>
                  </CalendarDateItem>
                );
              }}
            />
          )}
        </CalendarContainer>
      </FlexRow>
    );
  }, [
    addMonths,
    date,
    datePickerLocale,
    datePickerMode,
    disabledDate,
    format,
    initDayJsDate,
    isSameDay,
    presetOptionsList,
    setPickerDate,
    subMonths,
  ]);

  const onReset = useCallback(() => {
    setDate(undefined);
    setDesktopTimeMinutes(undefined);
    setMobileDateWithTime(undefined);
    onApply(null);
    visibilityHandler.close();
  }, [onApply, visibilityHandler]);

  const datePickerTriggerProps = useMemo(
    () => ({
      value: inputValue ? initDayJsDate(inputValue) : null,
      label,
      showSuffixIcon,
      allowClear,
      placeholderText: placeholder,
      'data-lgg-id': testId,
      onChange: (date) => {
        onApply(date ? date.toDate() : null);
      },
      hasError: hasError,
      formatHandler: (date) => format(date.toDate(), inputFormat),
    }),
    [
      inputValue,
      initDayJsDate,
      label,
      showSuffixIcon,
      allowClear,
      placeholder,
      testId,
      hasError,
      onApply,
      format,
      inputFormat,
    ],
  );

  const mobileDatePicker = useMemo(
    () => (
      <>
        <LggDatePickerInput
          {...datePickerTriggerProps}
          formatHandler={(date) => format(date.toDate(), 'MMM D, YYYY')}
          value={date ?? null}
          onClick={datePickerVisibilityHandler.show}
          isActive={datePickerVisibilityHandler.visible}
        />
        <ModalDatePickerModal
          visible={datePickerVisibilityHandler.visible}
          onClose={datePickerVisibilityHandler.close}
        >
          {calendarOverlay}
        </ModalDatePickerModal>
      </>
    ),
    [
      calendarOverlay,
      date,
      datePickerTriggerProps,
      datePickerVisibilityHandler.close,
      datePickerVisibilityHandler.show,
      datePickerVisibilityHandler.visible,
      format,
    ],
  );

  const inputs = useMemo(() => {
    const baseDate = startOfDay(new Date());
    const getTimeOption = (totalMinutes: number) => ({
      value: totalMinutes,
      label: format(addMinutes(baseDate, totalMinutes), 'h:mm A'),
    });
    const presetTimeOptions = range(0, 96).map((index) => getTimeOption(index * 15));
    const timeOptions = getTimeOptionsMatchingSearch({
      presetTimeOptions,
      searchText: timeSearchText,
      getTimeOption,
    });

    return (
      <>
        {breakpointUpMd ? (
          <>
            <Dropdown
              overlayClassName="context-menu"
              visible={datePickerVisibilityHandler.visible}
              onVisibleChange={(isVisible) => {
                if (!isVisible) {
                  datePickerVisibilityHandler.close();
                }
              }}
              placement="bottomLeft"
              trigger={['click']}
              overlay={calendarOverlay}
            >
              <LggDatePickerInput
                value={date ? date : null}
                onClick={datePickerVisibilityHandler.show}
                isActive={datePickerVisibilityHandler.visible}
                data-lgg-id="popover-date-picker"
                placeholderText={t('common:inputPlaceholder.date')}
                onChange={(date) => {
                  // field's clear button was clicked
                  if (date === null) {
                    // Clear the date field and close the date picker
                    setDate(undefined);
                    datePickerVisibilityHandler.close();
                  }
                }}
              />
            </Dropdown>
            <StyledSelect
              onChange={(value) => {
                setDesktopTimeMinutes(value?.value);
              }}
              value={
                desktopTimeMinutes !== undefined
                  ? getTimeOption(desktopTimeMinutes)
                  : null
              }
              options={timeOptions}
              placeholder={t('common:inputPlaceholder.time')}
              name="popover-time-picker"
              onSearchTextChange={setTimeSearchText}
              disableInternalSearch
            />
          </>
        ) : (
          <>
            {mobileDatePicker}
            <MobileWheelTimePicker
              value={mobileDateWithTime ? mobileDateWithTime.toDate() : undefined}
              onApply={(dateWithTime) => {
                if (dateWithTime) {
                  setMobileDateWithTime(initDayJsDate(dateWithTime));
                } else {
                  setMobileDateWithTime(undefined);
                }
              }}
            />
          </>
        )}
      </>
    );
  }, [
    addMinutes,
    breakpointUpMd,
    calendarOverlay,
    date,
    datePickerVisibilityHandler,
    desktopTimeMinutes,
    format,
    initDayJsDate,
    mobileDatePicker,
    mobileDateWithTime,
    startOfDay,
    t,
    timeSearchText,
  ]);

  const footerContent = useMemo(() => {
    const isDateSelected = date !== undefined;
    const isDesktopTimeOptionSelected = desktopTimeMinutes !== undefined;
    const isMobileTimeSelected = mobileDateWithTime !== undefined;

    return (
      <FooterContainer>
        <FooterButton
          size="regular"
          type="button"
          variant="default"
          data-lgg-id="reset-button"
          onClick={onReset}
        >
          {t('common:clear')}
        </FooterButton>
        <FooterButton
          size="regular"
          variant="primary"
          type="button"
          data-lgg-id="apply-button"
          disabled={
            !isDateSelected ||
            (breakpointUpMd ? !isDesktopTimeOptionSelected : !isMobileTimeSelected)
          }
          onClick={onSubmit}
        >
          {t('common:apply')}
        </FooterButton>
      </FooterContainer>
    );
  }, [
    onReset,
    t,
    date,
    breakpointUpMd,
    desktopTimeMinutes,
    mobileDateWithTime,
    onSubmit,
  ]);

  const desktopContent = useMemo(() => {
    return (
      <Dropdown
        overlayClassName="context-menu"
        visible={visibilityHandler.visible}
        getPopupContainer={(triggerElement) => triggerElement}
        onVisibleChange={(isVisible) => {
          if (!isVisible) {
            if (inputValue) {
              setDate(initDayJsDate(inputValue));
              const { minutes } = getDatesDifferenceInUnits(
                startOfDay(inputValue),
                inputValue,
              );

              setDesktopTimeMinutes(minutes);
            } else {
              setDate(undefined);
              setDesktopTimeMinutes(undefined);
            }

            visibilityHandler.close();
            setDatePickerMode('month');
          }
        }}
        placement="bottomLeft"
        trigger={['click']}
        overlay={
          allowTimeSelection ? (
            <DateAndTimePickerMobileContainer>
              <HeaderContainer>{t('common:inputPlaceholder.date')}</HeaderContainer>
              <BodyContainer>{inputs}</BodyContainer>
              {footerContent}
            </DateAndTimePickerMobileContainer>
          ) : (
            calendarOverlay
          )
        }
      >
        <LggDatePickerInput
          {...datePickerTriggerProps}
          onClick={visibilityHandler.show}
          isActive={visibilityHandler.visible}
        />
      </Dropdown>
    );
  }, [
    allowTimeSelection,
    calendarOverlay,
    datePickerTriggerProps,
    footerContent,
    initDayJsDate,
    inputValue,
    inputs,
    startOfDay,
    t,
    visibilityHandler,
  ]);

  const mobileContent = useMemo(
    () =>
      allowTimeSelection ? (
        <>
          <LggDatePickerInput
            {...datePickerTriggerProps}
            onClick={visibilityHandler.show}
            isActive={visibilityHandler.visible}
          />
          <BottomDrawer
            visible={visibilityHandler.visible}
            title={t('common:inputPlaceholder.date')}
            onClose={visibilityHandler.close}
            footer={footerContent}
            data-lgg-id="lgg-mobile-range-picker"
          >
            {allowTimeSelection ? <BodyContainer>{inputs}</BodyContainer> : null}
          </BottomDrawer>
        </>
      ) : (
        mobileDatePicker
      ),
    [
      allowTimeSelection,
      datePickerTriggerProps,
      footerContent,
      inputs,
      mobileDatePicker,
      t,
      visibilityHandler.close,
      visibilityHandler.show,
      visibilityHandler.visible,
    ],
  );

  return breakpointUpMd ? (
    <>
      {visibilityHandler.visible && <NoScrollOverlay />}
      {desktopContent}
    </>
  ) : (
    mobileContent
  );
};
